import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

/* CARD COMPONENT */
const AboutCard = ({ card, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      style={{
        transform: "translate3d(0,0,0)",
        willChange: "transform, opacity",
        backfaceVisibility: "hidden",
      }}
    >
      <div
        className="p-7 rounded-2xl bg-gray-50 border border-gray-100
        hover:translate-x-2 transition-all duration-300
        hover:border-blue-400 hover:shadow-xl group"
      >
        <div className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">
          {card.icon}
        </div>

        <h4 className="text-lg font-black text-gray-900 mb-2">{card.title}</h4>

        <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
      </div>
    </motion.div>
  );
};

/* MAIN SECTION */
const AboutSection = () => {
  const cards = [
    {
      icon: "🎯",
      title: "Precision First",
      desc: "Every label printed to exact specification with zero-tolerance for error.",
    },
    {
      icon: "⚡",
      title: "Fast Turnaround",
      desc: "Express production lines for urgent bulk orders without compromising quality.",
    },
    {
      icon: "🌿",
      title: "Eco-Friendly Materials",
      desc: "Sustainable substrates and inks that meet global environmental standards.",
    },
  ];

  const textRef = useRef(null);
  const textInView = useInView(textRef, { once: true, margin: "-80px" });

  return (
    <section id="about" className="py-20 sm:py-24 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        {/* LEFT CARDS */}
        <div className="flex flex-col gap-5">
          {cards.map((card, i) => (
            <AboutCard key={i} card={card} index={i} />
          ))}
        </div>

        {/* RIGHT TEXT */}
        <motion.div
          ref={textRef}
          initial={{ opacity: 0, x: 30 }}
          animate={textInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{
            transform: "translate3d(0,0,0)",
            willChange: "transform, opacity",
            backfaceVisibility: "hidden",
          }}
        >
          {/* Tag */}
          <div className="text-xs font-black text-blue-600 mb-6 border border-blue-600/20 px-4 py-2 rounded-full w-fit bg-blue-50 uppercase tracking-widest">
            About Sahajink
          </div>

          {/* Title */}
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-[1.1] mb-8 tracking-tight">
            Your Trusted Label <br />
            <span className="text-blue-600">Manufacturing Partner.</span>
          </h2>

          {/* Description */}
          <div className="text-[14px] space-y-6 text-gray-600 sm:text-lg leading-relaxed mb-10">
            <p>
              Sahajink is a premier label printing company in Surat, Gujarat, with over 4 years of
              experience delivering high-quality custom labels to businesses
              across India and international markets.
            </p>

            <p>
              We combine cutting-edge printing technology with meticulous
              quality control to produce labels that define your brand and
              perform in any environment.
            </p>
          </div>

          {/* List */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {[
              "ISO 9001:2015 Certified Manufacturer",
              "Food-Safe & Pharma-Grade Labels",
              "Pan-India & International Delivery",
              "Custom Branding Specialists Surat",
            ].map((item) => (
              <li
                key={item}
                className="flex items-center gap-3 text-gray-800 font-bold text-sm"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[10px]">
                  ✔
                </span>

                {item}
              </li>
            ))}
          </ul>

          {/* Button */}
          <a
            href="#contact"
            className="inline-block py-4 px-6 sm:px-10 sm:py-5 bg-blue-600 text-white rounded-full
            hover:bg-blue-500 transition-all duration-300 font-bold uppercase tracking-widest text-[12px] sm:text-sm shadow-xl shadow-blue-600/20"
          >
            Work With Us
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
